import React, { useEffect, useState } from 'react';

type Props = {
    value?: string;
    onChange: (time: string) => void;
    onErrorChange?: (error: string) => void;
    maxMinutes?: number;
    disabled?: boolean;
    styles?: any;
    maxMenuHeight?: number;
};

const pad = (n: number) => String(n).padStart(2, '0');

const parseToMinutes = (value?: string): number => {
    if (!value) return 0;
    const v = value.trim().toLowerCase();
    const ampm = v.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
    if (ampm) {
        let h = parseInt(ampm[1], 10) % 12;
        const m = parseInt(ampm[2], 10);
        if (ampm[3] === 'pm') h += 12;
        return h * 60 + m;
    }
    const hms = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (hms) return parseInt(hms[1], 10) * 60 + parseInt(hms[2], 10);
    return 0;
};

const toHHmm = (mins: number) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

const humanize = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h} hr ${m} min`;
    if (h) return `${h} hr`;
    return `${m} min`;
};

const BreakDurationField: React.FC<Props> = ({ value, onChange, onErrorChange, maxMinutes = 8 * 60, disabled = false, }) => {
    const total = parseToMinutes(value);
    const [hrStr, setHrStr] = useState('0');
    const [minStr, setMinStr] = useState('00');
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState<string>('');
    const updateError = (msg: string) => {
        setError(msg); 
        if (onErrorChange) {
            onErrorChange(msg);
        }
    };

    useEffect(() => {
        if (!value) {
            setHrStr('0');
            setMinStr('00');
            return;
        }   

        const t = parseToMinutes(value);
        setHrStr(String(Math.floor(t / 60)));
        setMinStr(pad(t % 60));
    }, [value]);

    const commit = (mins: number) => {
        const clamped = Math.max(0, Math.min(maxMinutes, mins));
        setHrStr(String(Math.floor(clamped / 60)));
        setMinStr(pad(clamped % 60));
        updateError('');
        onChange(toHHmm(clamped));
    };

    const commitFromStrings = (h: string, m: string) => {
        const rawH = Math.max(0, parseInt(h, 10) || 0);
        const rawM = Math.max(0, parseInt(m, 10) || 0);

        const rolledMinutes = rawM % 60;
        const carriedHours = Math.floor(rawM / 60);
        const rawTotal = (rawH + carriedHours) * 60 + rolledMinutes;
        setHrStr(String(rawH + carriedHours));
        setMinStr(pad(rolledMinutes));

        if (rawTotal > maxMinutes) {
            updateError(`Break duration can't exceed ${humanize(maxMinutes)}.`); // 3. YAHAN CHANGE
            return;
        }

        if (rawTotal <= 0) {
            updateError("Set a duration above 0 to save this break."); // 3. YAHAN CHANGE
            return;
        }

        updateError(''); 
        commit(rawTotal);
    };

    const step = (delta: number) => {
        const next = total + delta;
        if (next > maxMinutes) {
            updateError(`Break duration can't exceed ${humanize(maxMinutes)}.`);
            return;
        }
        if (next <= 0) {
            updateError("Set a duration above 0 to save this break.");
            return;
        }
        updateError('');
        commit(next);
    };

    const primary = 'var(--bs-primary)';
    const errorColor = '#F1416C';
    const borderColor = error ? errorColor : focused ? primary : '#E1E3EA';
    const ring = error ? '0 0 0 0.2rem rgba(241, 65, 108, 0.15)' : focused ? '0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.18)' : 'none';

    const segInput: React.CSSProperties = {
        width: 22,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1,
        color: '#181C32',
        textAlign: 'left',
        fontVariantNumeric: 'tabular-nums',
    };

    const stepBtn: React.CSSProperties = {
        flex: 1,
        width: 34,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        color: '#7E8299',
        cursor: disabled ? 'not-allowed' : 'pointer',
    };

    return (
        <div>
            <div
                className="d-flex align-items-stretch"
                style={{
                    width: '100%',
                    height: 42,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '0.475rem',
                    background: '#fff',
                    boxShadow: ring,
                    overflow: 'hidden',
                    transition: 'border-color .15s, box-shadow .15s',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <div className="d-flex align-items-center ps-4 pe-3" style={{ gap: '2px' }}>
                    <input
                        type="text"
                        inputMode="numeric"
                        aria-label="Hours"
                        disabled={disabled}
                        value={hrStr}
                        style={segInput}
                        onFocus={() => setFocused(true)}
                        onBlur={() => {
                            setFocused(false);
                            commitFromStrings(hrStr, minStr);
                        }}
                        onChange={(e) => setHrStr(e.target.value.replace(/\D/g, '').slice(0, 2))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#181C32', marginRight: '6px' }}>
                        hr
                    </span>
                    :

                    <input
                        type="text"
                        inputMode="numeric"
                        aria-label="Minutes"
                        disabled={disabled}
                        value={minStr}
                        style={{ ...segInput, width: 25 }}
                        onFocus={() => setFocused(true)}
                        onBlur={() => {
                            setFocused(false);
                            commitFromStrings(hrStr, minStr);
                        }}
                        onChange={(e) => setMinStr(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#181C32' }}>
                        min
                    </span>
                </div>

                <div className="ms-auto d-flex flex-column" style={{ borderLeft: '1px solid #E1E3EA' }}>
                    <button
                        type="button"
                        tabIndex={-1}
                        disabled={disabled}
                        style={{ ...stepBtn, borderBottom: '1px solid #E1E3EA' }}
                        onClick={() => step(1)}
                        aria-label="Increase 5 minutes"
                    >
                        <i className="bi bi-chevron-up fs-8" />
                    </button>
                    <button
                        type="button"
                        tabIndex={-1}
                        disabled={disabled}
                        style={stepBtn}
                        onClick={() => step(-1)}
                        aria-label="Decrease 5 minutes"
                    >
                        <i className="bi bi-chevron-down fs-8" />
                    </button>
                </div>
            </div>

            {error && (
                <div className="mt-2 fs-8" style={{ color: errorColor }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default BreakDurationField;